/**
 * Serviço de detecção de material baseado em dimensões.
 *
 * ALGORITMO MELHORADO:
 * 1. Encontrar o MAIOR objeto (bounding box máximo)
 * 2. Ordenar regras por área (menor → maior) para otimizar custo
 * 3. Encontrar a PRIMEIRA regra onde o objeto CABE (com tolerância)
 * 4. Verificar com rotação (objeto pode caber rotacionado)
 * 5. Calcular confiança baseada na "folga" disponível
 *
 * PRINCÍPIO: O material é identificado pela capacidade máxima do formato,
 * não pelo volume do pedido. O que importa é se o maior objeto cabe.
 */

import { sendToTerminal } from '../../../../lib/terminalLogger';
import { LogCategory } from '../../../../config/logging';
import type { ProductRule, OrderObject, MaterialDetectionResult } from '../types';

/**
 * Constantes de configuração do algoritmo
 */
const DETECTION_CONFIG = {
  /** Tolerância em cm para considerar que objeto cabe no envelope */
  TOLERANCE_CM: 2,
  /** Confiança mínima para considerar detecção válida */
  MIN_CONFIDENCE: 30,
  /** Confiança base quando objeto cabe perfeitamente */
  BASE_CONFIDENCE: 60,
  /** Multiplicador para calcular confiança baseada na folga */
  CONFIDENCE_MULTIPLIER: 200,
};

/**
 * Resultado intermediário do cálculo de fit
 */
interface FitResult {
  /** Se o objeto cabe na regra */
  fits: boolean;
  /** Se precisou rotacionar para caber */
  rotated: boolean;
  /** Folga na largura (%) */
  widthSlack: number;
  /** Folga na altura (%) */
  heightSlack: number;
  /** Menor folga entre largura e altura (%) */
  minSlack: number;
}

/**
 * Verifica se um objeto cabe em uma regra (com tolerância e rotação)
 */
function checkFit(
  objectWidth: number,
  objectHeight: number,
  ruleWidth: number,
  ruleHeight: number,
  tolerance: number = DETECTION_CONFIG.TOLERANCE_CM
): FitResult {
  const ruleWidthWithTolerance = ruleWidth + tolerance;
  const ruleHeightWithTolerance = ruleHeight + tolerance;

  // Verificar se cabe na orientação normal
  const fitsNormal =
    objectWidth <= ruleWidthWithTolerance &&
    objectHeight <= ruleHeightWithTolerance;

  // Verificar se cabe rotacionado (90°)
  const fitsRotated =
    objectHeight <= ruleWidthWithTolerance &&
    objectWidth <= ruleHeightWithTolerance;

  if (fitsNormal) {
    // Calcular folga (quanto sobra em %)
    const widthSlack = (ruleWidth - objectWidth) / ruleWidth;
    const heightSlack = (ruleHeight - objectHeight) / ruleHeight;
    return {
      fits: true,
      rotated: false,
      widthSlack: Math.max(0, widthSlack),
      heightSlack: Math.max(0, heightSlack),
      minSlack: Math.max(0, Math.min(widthSlack, heightSlack)),
    };
  }

  if (fitsRotated) {
    // Calcular folga com objeto rotacionado
    const widthSlack = (ruleWidth - objectHeight) / ruleWidth;
    const heightSlack = (ruleHeight - objectWidth) / ruleHeight;
    return {
      fits: true,
      rotated: true,
      widthSlack: Math.max(0, widthSlack),
      heightSlack: Math.max(0, heightSlack),
      minSlack: Math.max(0, Math.min(widthSlack, heightSlack)),
    };
  }

  // Não cabe de nenhuma forma
  return {
    fits: false,
    rotated: false,
    widthSlack: 0,
    heightSlack: 0,
    minSlack: 0,
  };
}

/**
 * Calcula a confiança da detecção baseada na folga
 *
 * - Folga > 20%: 100% confiança (objeto cabe confortavelmente)
 * - Folga = 0%: 60% confiança (objeto cabe justo)
 * - Valores intermediários são interpolados
 */
function calculateConfidence(minSlack: number): number {
  // Confiança = base + (folga * multiplicador)
  // Com folga de 0%: 60% confiança
  // Com folga de 20%: 100% confiança
  const confidence =
    DETECTION_CONFIG.BASE_CONFIDENCE +
    minSlack * DETECTION_CONFIG.CONFIDENCE_MULTIPLIER;

  return Math.min(100, Math.round(confidence));
}

/**
 * Encontra o maior objeto por dimensão máxima (não por área)
 * Isso garante que o envelope encontrado comporta o objeto mais "problemático"
 */
function findLargestObject(objects: OrderObject[]): OrderObject {
  return objects.reduce((largest, current) => {
    // Usar a maior dimensão de cada objeto para comparação
    const largestMaxDim = Math.max(largest.width, largest.height);
    const currentMaxDim = Math.max(current.width, current.height);
    return currentMaxDim > largestMaxDim ? current : largest;
  });
}

/**
 * Ordena regras por área (menor primeiro)
 * Isso garante que encontramos o material mais econômico que comporta o objeto
 */
function sortRulesByArea(rules: ProductRule[]): ProductRule[] {
  return [...rules].sort((a, b) => {
    const areaA = (a.dimensoes?.largura_cm || 0) * (a.dimensoes?.altura_cm || 0);
    const areaB = (b.dimensoes?.largura_cm || 0) * (b.dimensoes?.altura_cm || 0);
    return areaA - areaB;
  });
}

/**
 * Detecta o material mais adequado para os objetos fornecidos.
 *
 * @param objects - Lista de objetos coletados (dimensões em CM)
 * @param rules - Lista de regras de produtos disponíveis
 * @returns Resultado da detecção ou null se nenhum material comporta
 */
export function detectMaterial(
  objects: OrderObject[],
  rules: ProductRule[]
): MaterialDetectionResult | null {
  if (objects.length === 0) {
    sendToTerminal('⚠️ [DETECT] Nenhum objeto fornecido', {}, LogCategory.BUSINESS);
    return null;
  }

  if (rules.length === 0) {
    sendToTerminal('⚠️ [DETECT] Nenhuma regra disponível', {}, LogCategory.BUSINESS);
    return null;
  }

  // 1. Encontrar o maior objeto
  const largestObject = findLargestObject(objects);
  const objectWidth = largestObject.width;
  const objectHeight = largestObject.height;
  const objectArea = objectWidth * objectHeight;

  sendToTerminal('📐 [DETECT] Maior objeto identificado', {
    nome: largestObject.nome,
    largura: objectWidth.toFixed(2),
    altura: objectHeight.toFixed(2),
    areaCm2: objectArea.toFixed(2),
  }, LogCategory.BUSINESS);

  // 2. Filtrar regras válidas (com dimensões) e ordenar por área
  const validRules = rules.filter(
    (r) => r.dimensoes?.largura_cm && r.dimensoes?.altura_cm
  );

  if (validRules.length === 0) {
    sendToTerminal('⚠️ [DETECT] Nenhuma regra com dimensões válidas', {
      totalRules: rules.length,
    }, LogCategory.BUSINESS);
    return null;
  }

  const sortedRules = sortRulesByArea(validRules);

  sendToTerminal('📋 [DETECT] Regras ordenadas por área', {
    count: sortedRules.length,
    ordem: sortedRules.map((r) => ({
      nome: r.produto?.nome_servico,
      area: (r.dimensoes!.largura_cm * r.dimensoes!.altura_cm).toFixed(0),
    })),
  }, LogCategory.BUSINESS);

  // 3. Encontrar a PRIMEIRA regra onde o objeto cabe
  for (const rule of sortedRules) {
    const ruleWidth = rule.dimensoes!.largura_cm;
    const ruleHeight = rule.dimensoes!.altura_cm;

    const fitResult = checkFit(
      objectWidth,
      objectHeight,
      ruleWidth,
      ruleHeight,
      DETECTION_CONFIG.TOLERANCE_CM
    );

    if (fitResult.fits) {
      const confidence = calculateConfidence(fitResult.minSlack);

      sendToTerminal('✅ [DETECT] Material encontrado!', {
        material: rule.produto?.nome_servico,
        regraId: rule.regra?.id,
        rotacionado: fitResult.rotated,
        folgaLargura: `${(fitResult.widthSlack * 100).toFixed(1)}%`,
        folgaAltura: `${(fitResult.heightSlack * 100).toFixed(1)}%`,
        confianca: `${confidence}%`,
      }, LogCategory.BUSINESS);

      // Verificar se confiança atinge o mínimo
      if (confidence >= DETECTION_CONFIG.MIN_CONFIDENCE) {
        return {
          rule,
          distance: 0, // Não usamos mais distância euclidiana
          confidence,
          largestObject,
          largestObjectAreaCm2: objectArea,
        };
      } else {
        sendToTerminal('⚠️ [DETECT] Confiança abaixo do mínimo', {
          material: rule.produto?.nome_servico,
          confianca: confidence,
          minimo: DETECTION_CONFIG.MIN_CONFIDENCE,
        }, LogCategory.BUSINESS);
      }
    } else {
      sendToTerminal('❌ [DETECT] Objeto não cabe', {
        material: rule.produto?.nome_servico,
        objetoLargura: objectWidth.toFixed(2),
        objetoAltura: objectHeight.toFixed(2),
        regraLargura: ruleWidth.toFixed(2),
        regraAltura: ruleHeight.toFixed(2),
        tolerancia: DETECTION_CONFIG.TOLERANCE_CM,
      }, LogCategory.BUSINESS);
    }
  }

  // 4. Nenhum material comporta o objeto
  sendToTerminal('⚠️ [DETECT] Nenhum material comporta o objeto', {
    objetoLargura: objectWidth.toFixed(2),
    objetoAltura: objectHeight.toFixed(2),
    maiorRegra: sortedRules[sortedRules.length - 1]?.produto?.nome_servico,
    maiorRegraLargura: sortedRules[sortedRules.length - 1]?.dimensoes?.largura_cm,
    maiorRegraAltura: sortedRules[sortedRules.length - 1]?.dimensoes?.altura_cm,
  }, LogCategory.BUSINESS);

  return null;
}

/**
 * Exportar configuração para uso em testes ou ajustes
 */
export { DETECTION_CONFIG };
